import React, { useEffect, useRef, useState } from 'react';import { Calendar, Loader2, MapPin, Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StaffDateGate
 *
 * Reusable filter strip that defers loading admin lists until the user has
 * picked a date range and either:
 *   • A specific staff member, OR
 *   • A geofence (in which case all staff inside it are loaded together).
 *
 * Filter rules:
 *   - "All geofences" + "ALL staff" → NOT allowed (would pull every staff
 *     across the org). User must narrow at least one.
 *   - One geofence selected         → staff dropdown gains an "ALL staff in
 *     this geofence" option at the top.
 *   - All geofences (none selected) → staff must be specifically searched.
 *
 * Props:
 *   value      : {
 *                  geo_fence_id: string|null,
 *                  geo_fence_name: string|null,
 *                  staff: {id,name,...}|null,    // id === '__ALL_IN_GEO__' for "all"
 *                  from: 'YYYY-MM-DD',
 *                  to:   'YYYY-MM-DD',
 *                }
 *   onChange   : (next) => void
 *   onApply    : () => void              // fires when "Load" is clicked
 *   loading    : boolean                  // spinner on Load button
 *   defaultDays: number                   // default range size if both dates empty (defaults to 7)
 *   extras     : ReactNode                // optional extra chips
 */
export default function StaffDateGate({ value, onChange, onApply, loading, defaultDays = 7, extras = null }) {
  const today = new Date().toISOString().slice(0, 10);
  const ndaysAgo = new Date(Date.now() - (defaultDays - 1) * 86400_000).toISOString().slice(0, 10);

  // Lazy-init defaults the first time the gate mounts.
  useEffect(() => {
    if (!value.from && !value.to) {
      onChange({ ...value, from: ndaysAgo, to: today });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Geo-fences master list (small, load once).
  const [geoOptions, setGeoOptions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/geofences`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setGeoOptions(await r.json());
      } catch {}
    })();
  }, []);

  // ---- Staff picker ----
  const [picker, setPicker] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const deb = useRef(null);

  useEffect(() => {
    if (deb.current) clearTimeout(deb.current);
    deb.current = setTimeout(async () => {
      if (!open) return;
      try {
        const token = localStorage.getItem('token');
        const qs = new URLSearchParams();
        if (picker.trim()) qs.set('q', picker.trim());
        if (value.geo_fence_id) qs.set('geo_fence_id', value.geo_fence_id);
        const r = await fetch(
          `${API_URL}/api/admin/staff-options${qs.toString() ? `?${qs.toString()}` : ''}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) return;
        const d = await r.json();
        setOptions(d.staff || []);
      } catch (_) {}
    }, 250);
    return () => deb.current && clearTimeout(deb.current);
  }, [picker, open, value.geo_fence_id]);

  const ALL_IN_GEO = '__ALL_IN_GEO__';
  const ALL_GLOBAL = '__ALL_GLOBAL__';
  const allInGeoOption = value.geo_fence_id && value.geo_fence_name
    ? {
        id: ALL_IN_GEO,
        name: `ALL staff in ${value.geo_fence_name}`,
        mobile: '',
        staff_code: '',
        department: '',
      }
    : null;
  // Global "every staff in every geofence" — only offered when NO geofence
  // is picked, so the admin must consciously pick it. Used by Unplan Visits
  // and any other report where the user explicitly wants the full org.
  const allGlobalOption = !value.geo_fence_id
    ? {
        id: ALL_GLOBAL,
        name: 'ALL staff across ALL geofences',
        mobile: '',
        staff_code: '',
        department: '',
      }
    : null;

  // When a specific geofence is picked, auto-select "ALL staff in geo" and
  // fire `onApply` on the next render so the parent sees the freshly-merged
  // state. With "All geofences" picked, we still wait for a manual staff
  // pick + Load click (per spec).
  const autoApplyPending = useRef(false);

  const handleGeoChange = (geo_fence_id) => {
    const next = geoOptions.find(g => g.id === geo_fence_id);
    if (geo_fence_id && next) {
      onChange({
        ...value,
        geo_fence_id,
        geo_fence_name: next.name,
        staff: {
          id: ALL_IN_GEO,
          name: `ALL staff in ${next.name}`,
          mobile: '', staff_code: '', department: '',
        },
      });
      autoApplyPending.current = true;
      return;
    }
    // Cleared geofence — drop the ALL pseudo-selection so the user must
    // search a specific staff again.
    onChange({
      ...value,
      geo_fence_id: null,
      geo_fence_name: null,
      staff: value.staff?.id === ALL_IN_GEO ? null : value.staff,
    });
  };

  useEffect(() => {
    if (
      autoApplyPending.current &&
      value.geo_fence_id &&
      value.staff?.id === ALL_IN_GEO &&
      value.from && value.to
    ) {
      autoApplyPending.current = false;
      onApply && onApply();
    }
  }, [value.geo_fence_id, value.staff?.id, value.from, value.to, onApply]);

  // Loadable when (1) dates set AND (2) either a specific staff OR
  // "all in geo" pseudo-option is chosen.
  const canLoad = !!(value.from && value.to && value.staff?.id);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap items-end gap-3" data-testid="staff-date-gate">
      {/* Geo-fence master dropdown */}
      <label className="text-xs text-gray-600 flex flex-col gap-1">
        <span className="font-medium text-gray-700">Geo Fence</span>
        <div className="relative">
          <MapPin size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <select
            value={value.geo_fence_id || ''}
            onChange={(e) => handleGeoChange(e.target.value)}
            className="pl-7 pr-2 py-1.5 border border-gray-200 rounded text-sm w-48 appearance-none bg-white"
            data-testid="staff-date-gate-geo"
          >
            <option value="">All geofences</option>
            {geoOptions.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
      </label>

      <label className="text-xs text-gray-600 flex flex-col gap-1">
        <span className="font-medium text-gray-700">From</span>
        <div className="relative">
          <Calendar size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="date"
            value={value.from || ''}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            className="pl-7 pr-2 py-1.5 border border-gray-200 rounded text-sm w-40"
            data-testid="staff-date-gate-from"
          />
        </div>
      </label>
      <label className="text-xs text-gray-600 flex flex-col gap-1">
        <span className="font-medium text-gray-700">To</span>
        <div className="relative">
          <Calendar size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="date"
            value={value.to || ''}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            className="pl-7 pr-2 py-1.5 border border-gray-200 rounded text-sm w-40"
            data-testid="staff-date-gate-to"
          />
        </div>
      </label>

      <div className="text-xs text-gray-600 flex flex-col gap-1 relative">
        <span className="font-medium text-gray-700">
          Staff{value.geo_fence_id ? ' (or ALL in geo)' : ''}
        </span>
        <div className="relative w-72 max-w-full">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={value.staff
              ? `${value.staff.name}${value.staff.staff_code ? ' · ' + value.staff.staff_code : ''}`
              : picker}
            onChange={(e) => {
              if (value.staff) { onChange({ ...value, staff: null }); setPicker(e.target.value); }
              else setPicker(e.target.value);
              setOpen(true);
            }}
            onFocus={() => { setOpen(true); setOptions([]); }}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            placeholder={value.geo_fence_id
              ? 'Pick ALL or search this geofence…'
              : 'Search name / mobile / staff ID…'}
            className="w-full pl-7 pr-7 py-1.5 border border-gray-200 rounded text-sm"
            data-testid="staff-date-gate-staff-search"
          />
          {value.staff && (
            <button
              onClick={() => { onChange({ ...value, staff: null }); setPicker(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              data-testid="staff-date-gate-staff-clear"
            >
              <X size={12} />
            </button>
          )}
          {open && (
            <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto z-30">
              {allInGeoOption && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onChange({ ...value, staff: allInGeoOption }); setOpen(false); setPicker(''); }}
                  className="block w-full px-3 py-2 text-left hover:bg-emerald-50 text-sm border-b border-emerald-100 bg-emerald-50/40"
                  data-testid="staff-date-gate-option-all"
                >
                  <div className="font-semibold text-emerald-700 inline-flex items-center gap-1">
                    <MapPin size={11} /> {allInGeoOption.name}
                  </div>
                  <div className="text-[11px] text-emerald-600">Load every staff in this geofence together</div>
                </button>
              )}
              {allGlobalOption && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onChange({ ...value, staff: allGlobalOption }); setOpen(false); setPicker(''); }}
                  className="block w-full px-3 py-2 text-left hover:bg-orange-50 text-sm border-b border-orange-100 bg-orange-50/40"
                  data-testid="staff-date-gate-option-all-global"
                >
                  <div className="font-semibold text-orange-700 inline-flex items-center gap-1">
                    <MapPin size={11} /> {allGlobalOption.name}
                  </div>
                  <div className="text-[11px] text-orange-600">
                    Heavy — pulls every staff across the org. Use for monthly audits.
                  </div>
                </button>
              )}
              {options.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {picker ? 'No matches.' : (allInGeoOption ? 'Or type to search a specific person…' : 'Type 2+ characters to search…')}
                </div>
              )}
              {options.map(s => (
                <button
                  key={s.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onChange({ ...value, staff: s }); setOpen(false); setPicker(''); }}
                  className="block w-full px-3 py-2 text-left hover:bg-indigo-50 text-sm"
                  data-testid={`staff-date-gate-option-${s.id}`}
                >
                  <div className="font-medium text-gray-900">{s.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {s.mobile}{s.staff_code ? ` · ${s.staff_code}` : ''}{s.department ? ` · ${s.department}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {extras}

      <button
        onClick={onApply}
        disabled={!canLoad || loading}
        className="ml-auto flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg"
        data-testid="staff-date-gate-load"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : null}
        {loading ? 'Loading…' : 'Load'}
      </button>
    </div>
  );
}

// Export so callers can identify the "ALL staff" pseudo-selections.
export const ALL_IN_GEO_ID = '__ALL_IN_GEO__';
export const ALL_GLOBAL_ID = '__ALL_GLOBAL__';

/**
 * Helper for callers: build the right scope params for backend requests.
 * Returns an object with one of:
 *   - `{staff_id, from_date, to_date}`         specific staff
 *   - `{geo_fence_id, from_date, to_date}`     ALL staff in a geofence
 *   - `{all_staff: 1, from_date, to_date}`     ALL staff across ALL geofences
 *     (the new global option — admins use it for org-wide audits)
 * Returns null if the gate is not yet valid for loading.
 */
export function gateScopeParams(value) {
  if (!value.from || !value.to || !value.staff?.id) return null;
  if (value.staff.id === ALL_GLOBAL_ID) {
    return { all_staff: '1', from_date: value.from, to_date: value.to };
  }
  if (value.staff.id === ALL_IN_GEO_ID && value.geo_fence_id) {
    return { geo_fence_id: value.geo_fence_id, from_date: value.from, to_date: value.to };
  }
  return { staff_id: value.staff.id, from_date: value.from, to_date: value.to };
}
